import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './styles.scss';
import { PlacementTab } from './PlacementTab';
import { SpawnTab } from './SpawnTab';
import {
  AdminCtx, AdminCtxValue, ConfirmOptions, Density, DensityCtx, ToastOptions, capOfAction, formatAgo, formatCountdown, formatUptime, useDebounced,
} from './context';
import { Icon } from './icons';
import {
  AdminPanelDetail, ALL_CAPS, CAP_LABELS, CapId, DebugTarget, NAV, NAV_GROUPS, PanelMode, ServerStatus, StaffAudit, StaffState, ViewId, WeatherView as WeatherData,
} from './types';
import { ConfirmDialog, IconButton, ViewHeader } from './ui';
import { OverviewView, ActivityEntry } from './views/OverviewView';
import { PlayersView } from './views/PlayersView';
import { HousingView, ModesView, TeleportView, WeatherView } from './views/WorldViews';
import { BroadcastView, ReportsView } from './views/ModerationViews';
import { DebugView, StatusView } from './views/ServerViews';
import { StaffView } from './staff/StaffView';

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (dev preview, Storybook): the dev preview answers through this event
    // eslint-disable-next-line no-console
    console.log('adminPanel sendMessage', payload);
    window.dispatchEvent(new CustomEvent('skymp5-front:outgoing', { detail: payload }));
  }
};

const CLOSE_MS = 180;
const PENDING_MS = 2500;
const BUSY_MAX_MS = 3500;
const MAX_TOASTS = 4;
const MAX_ACTIVITY = 30;
const DENSITY_KEY = 'skymp5-admin-density';

interface Toast {
  id: number;
  kind: 'ok' | 'err' | 'info';
  text: string;
}

interface Pending {
  toastId: number;
  action: string;
}

const readDensity = (): Density => {
  try {
    return window.localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch (e) {
    return 'comfortable';
  }
};

const writeDensity = (d: Density): void => {
  try {
    window.localStorage.setItem(DENSITY_KEY, d);
  } catch (e) {
    /* private mode or blocked storage: the choice lasts for the session only */
  }
};

// The panel is also previewed without a server (dev preview): fill in what is missing
const normalizeDetail = (raw: any): AdminPanelDetail => {
  const d = raw || {};
  const me = d.me && Array.isArray(d.me.caps) ? (d.me.caps as CapId[]) : null;
  return {
    players: Array.isArray(d.players) ? d.players : [],
    locations: Array.isArray(d.locations) ? d.locations : [],
    modes: Array.isArray(d.modes) ? d.modes : [],
    reports: Array.isArray(d.reports) ? d.reports : [],
    houses: Array.isArray(d.houses) ? d.houses : [],
    // A server that does not send the caps yet: every admin could do everything but manage the staff
    me: { caps: me || ALL_CAPS.filter((c) => c !== 'staff') },
  };
};

const Ago: React.FC<{ at: number }> = ({ at }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 10000);
    return () => window.clearInterval(t);
  }, []);
  return <>{formatAgo(at, now)}</>;
};

const isTypingTarget = (t: EventTarget | null): boolean => {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
};

interface Hit {
  kind: 'player' | 'location';
  key: string;
  label: string;
  sub: string;
  online?: boolean;
  profileId?: number;
}

const AdminPanel = () => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<AdminPanelDetail | null>(null);
  const [detailAt, setDetailAt] = useState(0);
  const [view, setView] = useState<ViewId>('overview');
  const [selected, setSelected] = useState<number | null>(null);
  const [locQuery, setLocQuery] = useState('');
  const [status, setStatus] = useState<ServerStatus | null>(null);
  const [statusAt, setStatusAt] = useState(0);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [debugTarget, setDebugTarget] = useState<DebugTarget | null>(null);
  const [staff, setStaff] = useState<StaffState | null>(null);
  const [staffAudit, setStaffAudit] = useState<StaffAudit | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmOptions | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchIdx, setSearchIdx] = useState(0);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [density, setDensity] = useState<Density>(readDensity);
  // Mode toggles shown at once, before the server confirms
  const [modeOverride, setModeOverride] = useState<Record<string, boolean>>({});

  const searchRef = useRef<HTMLInputElement>(null);
  const identityRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);
  const closeTimer = useRef(0);
  const busyTimer = useRef(0);
  const toastId = useRef(1);
  const activityId = useRef(1);
  const toastTimers = useRef<Record<number, number>>({});
  const pending = useRef<Pending | null>(null);
  const prevView = useRef<ViewId | null>(null);
  const refreshTimer = useRef(0);
  const escapeStack = useRef<Array<() => void>>([]);
  const detailRef = useRef<AdminPanelDetail | null>(null);
  detailRef.current = detail;

  // The caps array of a fresh packet is a new object every time: keep the same one while the content is
  // the same, so the context (and everything that reads it) does not change on every refresh
  const capsKey = detail ? detail.me.caps.join(',') : '';
  const caps: CapId[] = useMemo(() => (capsKey ? (capsKey.split(',') as CapId[]) : []), [capsKey]);
  const capsRef = useRef<CapId[]>(caps);
  capsRef.current = caps;
  const can = useCallback((cap: CapId | null | undefined): boolean => !cap || capsRef.current.indexOf(cap) !== -1, []);

  const allowed = (id: ViewId): boolean => {
    const item = NAV.find((n) => n.id === id);
    return !!item && (!item.cap || caps.indexOf(item.cap) !== -1);
  };
  const activeView: ViewId = allowed(view) ? view : 'overview';

  // ---- the thin progress bar: on while a request is waiting for the server ----

  const markBusy = useCallback(() => {
    setBusy(true);
    window.clearTimeout(busyTimer.current);
    busyTimer.current = window.setTimeout(() => setBusy(false), BUSY_MAX_MS);
  }, []);

  const markIdle = useCallback(() => {
    window.clearTimeout(busyTimer.current);
    setBusy(false);
  }, []);

  // ---- toasts ------------------------------------------------------------

  const dismissToast = useCallback((id: number) => {
    window.clearTimeout(toastTimers.current[id]);
    delete toastTimers.current[id];
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback((text: string, kind: 'ok' | 'err' | 'info'): number => {
    const id = toastId.current++;
    setToasts((list) => list.concat({ id, kind, text }).slice(-MAX_TOASTS));
    toastTimers.current[id] = window.setTimeout(() => dismissToast(id), kind === 'err' ? 6000 : kind === 'ok' ? 4000 : PENDING_MS);
    return id;
  }, [dismissToast]);

  const clearPending = useCallback(() => {
    if (pending.current) {
      dismissToast(pending.current.toastId);
      pending.current = null;
    }
  }, [dismissToast]);

  // ---- context for the views: the functions never change, only the caps can ----

  const actions = useMemo(() => {
    const canAction = (action: string): boolean => can(capOfAction(action));
    return {
      canAction,
      send: (payload: Record<string, unknown>) => {
        markBusy();
        send(payload);
      },
      act: (action: string, extra?: Record<string, unknown>, pendingLabel?: string) => {
        if (!canAction(action)) {
          pushToast('Votre rang ne permet pas cette action.', 'err');
          return;
        }
        if (action === 'toggleMode' && extra && typeof extra.mode === 'string') {
          const id = extra.mode;
          const cur = detailRef.current ? detailRef.current.modes.find((m) => m.id === id) : undefined;
          setModeOverride((o) => ({ ...o, [id]: !(id in o ? o[id] : cur ? cur.active : false) }));
        }
        markBusy();
        send({ action, ...(extra || {}) });
        clearPending();
        pending.current = { toastId: pushToast(`${pendingLabel || 'Action'} : envoi en cours...`, 'info'), action };
      },
      toast: (text: string, opts?: ToastOptions) => {
        pushToast(text, (opts && opts.kind) || 'info');
      },
      confirm: (opts: ConfirmOptions) => setConfirmState(opts),
      goto: (id: ViewId, opts?: { locationQuery?: string; profileId?: number }) => {
        const item = NAV.find((n) => n.id === id);
        if (!item || !can(item.cap)) return;
        if (opts && opts.locationQuery !== undefined) setLocQuery(opts.locationQuery);
        if (opts && opts.profileId !== undefined) setSelected(opts.profileId);
        setSearchOpen(false);
        setView(id);
      },
      onEscape: (handler: () => void) => {
        escapeStack.current.push(handler);
        return () => {
          const i = escapeStack.current.lastIndexOf(handler);
          if (i !== -1) escapeStack.current.splice(i, 1);
        };
      },
    };
  }, [can, pushToast, clearPending, markBusy]);

  const ctx = useMemo<AdminCtxValue>(() => ({ caps, can, ...actions }), [caps, can, actions]);

  // ---- events from the client service -------------------------------------

  useEffect(() => {
    const showHandler = (e: Event) => {
      const d = normalizeDetail((e as CustomEvent).detail);
      const first = !openRef.current;
      setDetail(d);
      setDetailAt(Date.now());
      setRefreshing(false);
      setModeOverride({});
      markIdle();
      window.clearTimeout(refreshTimer.current);
      if (pending.current && pending.current.action === 'toggleMode') {
        clearPending();
        pushToast('Mode mis à jour.', 'ok');
      }
      if (first) {
        openRef.current = true;
        window.clearTimeout(closeTimer.current);
        setMounted(true);
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => setOpen(true)));
        // The status runs for as long as the panel is open (the top bar shows it)
        send({ action: 'requestServerStatus' });
        if (d.me.caps.indexOf('reports') !== -1) send({ action: 'reportsRefresh' });
        if (d.me.caps.indexOf('weather') !== -1) send({ action: 'weatherList', target: '' });
      }
    };
    const hideHandler = () => {
      if (!openRef.current) return;
      openRef.current = false;
      setOpen(false);
      setQuery('');
      setSearchOpen(false);
      setIdentityOpen(false);
      setConfirmState(null);
      prevView.current = null;
      markIdle();
      send({ action: 'debugTabClosed' });
      send({ action: 'serverStatusClosed' });
      window.clearTimeout(closeTimer.current);
      closeTimer.current = window.setTimeout(() => setMounted(false), CLOSE_MS);
    };
    const debugHandler = (e: Event) => setDebugTarget((e as CustomEvent).detail as DebugTarget | null);
    const statusHandler = (e: Event) => {
      setStatus((e as CustomEvent).detail as ServerStatus);
      setStatusAt(Date.now());
    };
    const weatherHandler = (e: Event) => {
      setWeather((e as CustomEvent).detail as WeatherData);
      markIdle();
      if (pending.current && /^(weather|time)/.test(pending.current.action)) {
        clearPending();
        pushToast('Météo et heure mises à jour.', 'ok');
      }
    };
    const staffHandler = (e: Event) => {
      const d = (e as CustomEvent).detail as Partial<StaffState>;
      markIdle();
      setStaff({
        canManage: !!d.canManage,
        owners: d.owners || [],
        members: d.members || [],
        roles: d.roles || [],
        capInfo: d.capInfo || [],
        audit: d.audit || [],
      });
    };
    const auditHandler = (e: Event) => {
      markIdle();
      setStaffAudit((e as CustomEvent).detail as StaffAudit);
    };
    const resultHandler = (e: Event) => {
      const d = (e as CustomEvent).detail as { ok: boolean; text: string };
      if (!d || !d.text) return;
      markIdle();
      clearPending();
      pushToast(d.text, d.ok ? 'ok' : 'err');
      setActivity((list) => [{ id: activityId.current++, at: Date.now(), ok: !!d.ok, text: d.text }].concat(list).slice(0, MAX_ACTIVITY));
    };

    window.addEventListener('skymp5-client:showAdminMenu', showHandler);
    window.addEventListener('skymp5-client:hideAdminMenu', hideHandler);
    window.addEventListener('skymp5-client:updateAdminDebug', debugHandler);
    window.addEventListener('skymp5-client:adminServerStatus', statusHandler);
    window.addEventListener('skymp5-client:adminWeather', weatherHandler);
    window.addEventListener('skymp5-client:adminStaff', staffHandler);
    window.addEventListener('skymp5-client:adminStaffAudit', auditHandler);
    window.addEventListener('skymp5-client:adminActionResult', resultHandler);
    return () => {
      window.removeEventListener('skymp5-client:showAdminMenu', showHandler);
      window.removeEventListener('skymp5-client:hideAdminMenu', hideHandler);
      window.removeEventListener('skymp5-client:updateAdminDebug', debugHandler);
      window.removeEventListener('skymp5-client:adminServerStatus', statusHandler);
      window.removeEventListener('skymp5-client:adminWeather', weatherHandler);
      window.removeEventListener('skymp5-client:adminStaff', staffHandler);
      window.removeEventListener('skymp5-client:adminStaffAudit', auditHandler);
      window.removeEventListener('skymp5-client:adminActionResult', resultHandler);
      window.clearTimeout(closeTimer.current);
      window.clearTimeout(refreshTimer.current);
      window.clearTimeout(busyTimer.current);
    };
  }, [clearPending, pushToast, markIdle]);

  // What entering a view asks of the client and the server
  useEffect(() => {
    if (!open) return;
    const prev = prevView.current;
    prevView.current = activeView;
    if (prev === 'debug' && activeView !== 'debug') send({ action: 'debugTabClosed' });
    if (activeView === 'debug' && prev !== 'debug') send({ action: 'debugTabOpen' });
    if (prev === activeView) return;
    if (activeView === 'reports') send({ action: 'reportsRefresh' });
    if (activeView === 'housing') send({ action: 'housingRefresh' });
    if (activeView === 'weather' || activeView === 'overview') {
      if (can('weather')) send({ action: 'weatherList', target: '' });
    }
    if (activeView === 'staff') {
      markBusy();
      send({ action: 'adminStaffRequest' });
    }
  }, [open, activeView, can, markBusy]);

  // ---- keyboard -----------------------------------------------------------

  const kb = useRef({ confirmOpen: false, searchOpen: false, identityOpen: false });
  kb.current = { confirmOpen: !!confirmState, searchOpen, identityOpen };

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const s = kb.current;
      if (e.key === 'Escape') {
        e.preventDefault();
        if (s.confirmOpen) setConfirmState(null);
        else if (s.searchOpen) {
          setSearchOpen(false);
          setQuery('');
          if (searchRef.current) searchRef.current.blur();
        } else if (s.identityOpen) setIdentityOpen(false);
        else if (escapeStack.current.length) escapeStack.current[escapeStack.current.length - 1]();
        else send({ action: 'close' });
        return;
      }
      if (e.key === '/' && !s.confirmOpen && !isTypingTarget(e.target) && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        if (searchRef.current) searchRef.current.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted]);

  useEffect(() => {
    if (!identityOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (identityRef.current && !identityRef.current.contains(e.target as Node)) setIdentityOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [identityOpen]);

  // ---- global search ------------------------------------------------------

  const debouncedQuery = useDebounced(query, 120);

  const hits = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    const out: Hit[] = [];
    if (!q || !detail) return out;
    if (caps.indexOf('players') !== -1) {
      let n = 0;
      for (const p of detail.players) {
        if (n >= 6) break;
        if ([p.n, p.d, String(p.p)].join(' ').toLowerCase().indexOf(q) === -1) continue;
        out.push({ kind: 'player', key: 'p' + p.p, label: p.n || '-', sub: p.online ? 'Joueur en ligne' : 'Joueur hors ligne', online: p.online, profileId: p.p });
        n++;
      }
    }
    if (caps.indexOf('teleport') !== -1) {
      let n = 0;
      for (const l of detail.locations) {
        if (n >= 6) break;
        if (l.name.toLowerCase().indexOf(q) === -1) continue;
        out.push({ kind: 'location', key: 'l' + l.name, label: l.name, sub: 'Lieu de téléportation' });
        n++;
      }
    }
    return out;
  }, [debouncedQuery, detail, caps]);

  const chooseHit = (i: number) => {
    const h = hits[i];
    if (!h) return;
    if (h.kind === 'player') ctx.goto('players', { profileId: h.profileId });
    else ctx.goto('teleport', { locationQuery: h.label });
    setQuery('');
    setSearchOpen(false);
    if (searchRef.current) searchRef.current.blur();
  };

  const onSearchKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSearchIdx((i) => Math.min(hits.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSearchIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      chooseHit(searchIdx);
    }
  };

  // ---- actions ------------------------------------------------------------

  const refreshAll = () => {
    setRefreshing(true);
    markBusy();
    window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(() => setRefreshing(false), 4000);
    send({ action: 'refresh' });
    if (can('reports')) send({ action: 'reportsRefresh' });
    if (can('weather')) send({ action: 'weatherList', target: '' });
    if (activeView === 'housing') send({ action: 'housingRefresh' });
    if (activeView === 'staff') send({ action: 'adminStaffRequest' });
  };

  const toggleDensity = () => {
    const next: Density = density === 'compact' ? 'comfortable' : 'compact';
    setDensity(next);
    writeDensity(next);
  };

  // The modes as shown: the server's, with the toggles not confirmed yet applied
  const viewModes: PanelMode[] = useMemo(
    () => (detail ? detail.modes.map((m) => (m.id in modeOverride ? { ...m, active: modeOverride[m.id] } : m)) : []),
    [detail, modeOverride]
  );

  if (!mounted || !detail) return null;

  const isOwner = caps.indexOf('staff') !== -1;
  const openReports = detail.reports.filter((r) => r.status === 'open').length;
  const onlineCount = detail.players.filter((p) => p.online).length;
  const visibleNav = NAV.filter((n) => !n.cap || caps.indexOf(n.cap) !== -1);
  const current = NAV.find((n) => n.id === activeView);
  const menuCaps = ALL_CAPS.filter((c) => c !== 'staff' && c !== 'console');
  const myMenuCount = menuCaps.filter((c) => caps.indexOf(c) !== -1).length;

  const badgeFor = (id: ViewId): { n: number; warn: boolean } | null => {
    if (id === 'reports' && openReports > 0) return { n: openReports, warn: true };
    if (id === 'players') return { n: onlineCount, warn: false };
    return null;
  };

  return (
    <AdminCtx.Provider value={ctx}>
      <DensityCtx.Provider value={density}>
        <div className={'admin-panel' + (open ? ' admin-panel--open' : '') + (density === 'compact' ? ' admin-panel--compact' : '')}>
          <div className="admin-panel__window" role="dialog" aria-modal="true" aria-label="Panel d'administration">
            <div className={'admin-panel__progress' + (busy ? ' admin-panel__progress--on' : '')} role="progressbar" aria-hidden={!busy} aria-label="Requête en cours">
              <span className="admin-panel__progress-bar" />
            </div>

            <header className="admin-panel__top">
              <div className="admin-panel__brand">
                <span className="admin-panel__title">Panel Admin</span>
              </div>

              <div className="admin-panel__gsearch">
                <Icon name="search" size={16} className="admin-panel__gsearch-icon" />
                <input
                  ref={searchRef}
                  className="admin-panel__input admin-panel__search-input"
                  type="search"
                  role="combobox"
                  aria-expanded={searchOpen && hits.length > 0}
                  aria-controls="ap-search-list"
                  aria-label="Recherche globale : joueurs et lieux"
                  placeholder="Rechercher un joueur ou un lieu"
                  value={query}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() => window.setTimeout(() => setSearchOpen(false), 150)}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSearchIdx(0);
                    setSearchOpen(true);
                  }}
                  onKeyDown={onSearchKey}
                />
                <kbd className="admin-panel__kbd" aria-hidden="true">/</kbd>
                {searchOpen && query.trim() ? (
                  <ul id="ap-search-list" className="admin-panel__search-results" role="listbox" aria-label="Résultats">
                    {hits.length === 0 ? (
                      <li className="admin-panel__search-empty">{query.trim() === debouncedQuery.trim() ? 'Aucun résultat' : 'Recherche...'}</li>
                    ) : (
                      hits.map((h, i) => (
                        <li
                          key={h.key}
                          role="option"
                          aria-selected={i === searchIdx}
                          className={'admin-panel__search-hit' + (i === searchIdx ? ' admin-panel__search-hit--active' : '')}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            chooseHit(i);
                          }}
                          onMouseEnter={() => setSearchIdx(i)}
                        >
                          {h.kind === 'player' ? <span className={'admin-panel__dot' + (h.online ? ' admin-panel__dot--online' : '')} /> : <Icon name="teleport" size={14} />}
                          <span className="admin-panel__cell admin-panel__cell--name">{h.label}</span>
                          <span className="admin-panel__cell admin-panel__cell--muted">{h.sub}</span>
                        </li>
                      ))
                    )}
                  </ul>
                ) : null}
              </div>

              <div className="admin-panel__pills" aria-label="État du serveur" role="status">
                <span className="admin-panel__pill" title="Cadence de la boucle de jeu">
                  <span className="admin-panel__pill-label">TPS</span>
                  <span className="admin-panel__pill-value">{status && status.tps != null ? status.tps.toFixed(1) : '-'}</span>
                </span>
                <span className="admin-panel__pill" title="Joueurs en ligne">
                  <span className="admin-panel__pill-label">En ligne</span>
                  <span className="admin-panel__pill-value">{status ? status.onlinePlayers : onlineCount}</span>
                </span>
                <span className="admin-panel__pill admin-panel__pill--uptime" title="Durée de fonctionnement du serveur">
                  <span className="admin-panel__pill-label">Actif</span>
                  <span className="admin-panel__pill-value">{status ? formatUptime(status.uptimeSec) : '-'}</span>
                </span>
                {status && status.restartInSec != null ? (
                  <span className="admin-panel__pill admin-panel__pill--alert" title="Redémarrage programmé">
                    <span className="admin-panel__pill-label">Redémarrage</span>
                    <span className="admin-panel__pill-value">{formatCountdown(status.restartInSec)}</span>
                  </span>
                ) : null}
              </div>

              <div className="admin-panel__top-actions">
                <span className="admin-panel__updated" title="Dernière mise à jour de la liste des joueurs">
                  {refreshing ? 'Actualisation...' : <>Mis à jour <Ago at={detailAt} /></>}
                </span>
                <IconButton icon="refresh" label="Actualiser les données" onClick={refreshAll} disabled={refreshing} />
                <IconButton
                  icon="density"
                  label={density === 'compact' ? 'Densité compacte (passer en confortable)' : 'Densité confortable (passer en compact)'}
                  onClick={toggleDensity}
                  active={density === 'compact'}
                />

                <div className="admin-panel__identity" ref={identityRef}>
                  <button
                    type="button"
                    className="admin-panel__identity-btn"
                    aria-haspopup="true"
                    aria-expanded={identityOpen}
                    onClick={() => setIdentityOpen((v) => !v)}
                  >
                    <span className={'admin-panel__role' + (isOwner ? ' admin-panel__role--owner' : '')}>{isOwner ? 'Propriétaire' : 'Staff'}</span>
                    <span className="admin-panel__identity-sub">{isOwner ? 'Toutes les permissions' : `${myMenuCount} permission${myMenuCount > 1 ? 's' : ''}`}</span>
                  </button>
                  {identityOpen ? (
                    <div className="admin-panel__popover" role="dialog" aria-label="Vos permissions">
                      <div className="admin-panel__popover-title">Vos permissions</div>
                      <ul className="admin-panel__perm-list">
                        {menuCaps.map((c) => {
                          const on = caps.indexOf(c) !== -1;
                          return (
                            <li key={c} className={'admin-panel__perm' + (on ? ' admin-panel__perm--on' : '')}>
                              <Icon name={on ? 'check' : 'minus'} size={14} />
                              <span>{CAP_LABELS[c].label}</span>
                            </li>
                          );
                        })}
                      </ul>
                      {isOwner ? <div className="admin-panel__hint">Gestion du staff : oui (propriétaire).</div> : null}
                    </div>
                  ) : null}
                </div>

                <IconButton icon="close" label="Fermer le panel (Échap)" onClick={() => send({ action: 'close' })} />
              </div>
            </header>

            <nav className="admin-panel__rail" aria-label="Sections du panel">
              {NAV_GROUPS.map((g) => {
                const items = visibleNav.filter((n) => n.group === g);
                if (!items.length) return null;
                return (
                  <div key={g} className="admin-panel__rail-group" role="group" aria-label={g}>
                    <div className="admin-panel__rail-title">{g}</div>
                    {items.map((n) => {
                      const badge = badgeFor(n.id);
                      return (
                        <button
                          key={n.id}
                          type="button"
                          title={n.hint}
                          aria-current={n.id === activeView ? 'page' : undefined}
                          className={'admin-panel__nav' + (n.id === activeView ? ' admin-panel__nav--active' : '')}
                          onClick={() => setView(n.id)}
                        >
                          <Icon name={n.icon} size={18} />
                          <span className="admin-panel__nav-label">{n.label}</span>
                          {badge ? <span className={'admin-panel__nav-badge' + (badge.warn ? ' admin-panel__nav-badge--warn' : '')}>{badge.n}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </nav>

            <main className="admin-panel__main" aria-label={current ? current.label : 'Panel'}>
              <div className="admin-panel__viewwrap" key={activeView}>
                {activeView === 'overview' ? <OverviewView detail={detail} modes={viewModes} status={status} weather={weather} activity={activity} /> : null}
                {activeView === 'players' ? <PlayersView players={detail.players} modes={viewModes} selected={selected} onSelect={setSelected} /> : null}
                {activeView === 'modes' ? <ModesView modes={viewModes} /> : null}
                {activeView === 'reports' ? <ReportsView reports={detail.reports} /> : null}
                {activeView === 'broadcast' ? <BroadcastView /> : null}
                {activeView === 'teleport' ? <TeleportView locations={detail.locations} initialQuery={locQuery} /> : null}
                {activeView === 'placement' ? (
                  <div className="admin-panel__view">
                    <ViewHeader title="Placement" subtitle="Objets, stations, boîtes aux lettres et objets masqués de la carte." />
                    <PlacementTab />
                  </div>
                ) : null}
                {activeView === 'spawns' ? (
                  <div className="admin-panel__view">
                    <ViewHeader title="Spawns" subtitle="Zones de créatures sauvages et réglages des donjons." />
                    <SpawnTab />
                  </div>
                ) : null}
                {activeView === 'weather' ? <WeatherView weather={weather} /> : null}
                {activeView === 'housing' ? <HousingView houses={detail.houses} /> : null}
                {activeView === 'status' ? <StatusView status={status} statusAt={statusAt} knownCount={detail.players.length} /> : null}
                {activeView === 'debug' ? <DebugView target={debugTarget} /> : null}
                {activeView === 'staff' ? <StaffView staff={staff} audit={staffAudit} /> : null}
              </div>
            </main>
          </div>

          <div className="admin-panel__toasts" aria-live="polite" aria-atomic="false">
            {toasts.map((t) => (
              <div key={t.id} className={'admin-panel__toast admin-panel__toast--' + t.kind} role={t.kind === 'err' ? 'alert' : 'status'}>
                <span>{t.text}</span>
                <button type="button" className="admin-panel__toast-close" aria-label="Fermer la notification" onClick={() => dismissToast(t.id)}>
                  <Icon name="close" size={14} />
                </button>
              </div>
            ))}
          </div>

          {confirmState ? (
            <ConfirmDialog
              title={confirmState.title}
              body={confirmState.body}
              confirmLabel={confirmState.confirmLabel}
              danger={confirmState.danger}
              onCancel={() => setConfirmState(null)}
              onConfirm={() => {
                const run = confirmState.onConfirm;
                setConfirmState(null);
                run();
              }}
            />
          ) : null}
        </div>
      </DensityCtx.Provider>
    </AdminCtx.Provider>
  );
};

export default AdminPanel;
